<script setup>
import { onMounted, onUnmounted } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import { kanbanColumnDefs, kanbanDefaultColDef, kanbanRowClassRules } from '../presentation/kanban/kanbanColumns';
import { kanbanLocaleText } from '../presentation/kanban/kanbanLocale';
import { useKanbanDashboard } from '../presentation/kanban/useKanbanDashboard';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const dashboard = useKanbanDashboard();

const {
  rowData,
  searchText,
  statusFilter,
  ultimaAtualizacao,
  isLoading,
  errorMessage,
  pageSize,
  onGridReady,
  exportToCsv,
  load,
  applyFilters,
  updatePageSize,
  start,
  stop,
} = dashboard;

onMounted(() => {
  start();
});

onUnmounted(() => {
  stop();
});
</script>

<template>
  <div class="kanban-container">
    <div class="header-section">
      <div class="title-area">
        <h1 class="title">SCE</h1>
        <p class="subtitle">Kanban de Notas</p>
      </div>

      <div class="controls-area">
        <div class="search-group">
          <div class="search-wrapper">
            <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input v-model="searchText" type="text" placeholder="Buscar NF, cliente, produto ou OP" class="global-search" @keyup.enter="applyFilters" />
          </div>

          <select v-model="statusFilter" class="status-select" @change="applyFilters">
            <option value="">Todos os status</option>
            <option value="pendente">Pendentes</option>
            <option value="bipado">Liberados</option>
          </select>

          <button @click="applyFilters" class="btn btn-refresh" :disabled="isLoading">
            <span>Filtrar</span>
          </button>

          <button @click="exportToCsv" class="btn btn-export">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <span>Exportar</span>
          </button>

          <button @click="load" class="btn btn-refresh" :disabled="isLoading">
            <svg class="icon" :class="{ 'spinning': isLoading }" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2.5">
              <path d="M23 4v6h-6"></path>
              <path d="M1 20v-6h6"></path>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            <span>{{ isLoading ? '...' : 'Atualizar' }}</span>
          </button>
        </div>

        <div class="status-indicator">
          <span class="pulse-dot"></span>
          <span class="update-text">Sincronizado: {{ ultimaAtualizacao }}</span>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="progress-bar">
      <div class="progress-bar-fill"></div>
    </div>

    <div v-if="errorMessage" class="error-banner">
      {{ errorMessage }}
    </div>

    <div class="grid-wrapper ag-theme-alpine">
      <AgGridVue :rowData="rowData" :columnDefs="kanbanColumnDefs" :defaultColDef="kanbanDefaultColDef" :pagination="true"
        :paginationPageSize="pageSize" :paginationPageSizeSelector="[50, 100, 200]" :animateRows="true"
        :localeText="kanbanLocaleText" :rowClassRules="kanbanRowClassRules" @grid-ready="onGridReady"
        @pagination-changed="updatePageSize($event.api.paginationGetPageSize())" style="width: 100%; height: 100%;" />
    </div>
  </div>
</template>

<style scoped>
.kanban-container {
  font-family: 'Inter', system-ui, sans-serif;
  padding: 32px 40px;
  background: #f1f5f9;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Barra de progresso superior */

.progress-bar {
  position: relative;
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  overflow: hidden;
  border-radius: 3px;
}

.progress-bar-fill {
  height: 100%;
  width: 30%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  animation: loadingMove 1s infinite;
}

.error-banner {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
}

@keyframes loadingMove {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(400%);
  }
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-bottom: 20px;
  border-bottom: 1px solid #cbd5e1;
}

.title {
  font-size: 32px;
  font-weight: 800;
  margin: 0;
  color: #0f172a;
  letter-spacing: -0.5px;
}

.subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.controls-area {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: end;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: end;
}

.status-select {
  min-height: 42px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: white;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
}

.metrics-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: end;
}

.metric-chip {
  background: #e2e8f0;
  color: #334155;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.btn-refresh {
  background: white;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.btn-refresh:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
}

.btn-export {
  background: #10b981;
  border: 1px solid #059669;
  color: white;
}

.btn-export:hover {
  background: #059669;
  transform: translateY(-1px);
}

.icon {
  width: 16px;
  height: 16px;
}

:deep(.row-bipado) {
  background-color: #e8f7ee !important;
}

:deep(.row-liberado) {
  background-color: #e0f2fe !important;
}

.spinning {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  color: #94a3b8;
}

.global-search {
  padding: 10px 12px 10px 38px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  width: 260px;
  outline: none;
  transition: 0.2s;
  font-size: 14px;
  background: white;
}

.global-search:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
}

.update-text {
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  font-family: monospace;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background-color: #10b981;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }

  70% {
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
}

.grid-wrapper {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  height: calc(100vh - 190px);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

/* =========================================================
   AG-GRID — TEMA BASE
   ========================================================= */
:deep(.ag-theme-alpine) {
  --ag-font-family: 'Inter', system-ui, sans-serif;
  --ag-font-size: 13px;
  --ag-header-height: 46px;
  --ag-row-height: 44px;

  --ag-background-color: #ffffff;
  --ag-header-background-color: #f8fafc;
  --ag-header-foreground-color: #475569;
  --ag-foreground-color: #334155;
  --ag-data-color: #1e293b;

  --ag-row-hover-color: #f8fafc;
  --ag-selected-row-background-color: #eff6ff;

  --ag-row-border-color: #f1f5f9;
  --ag-borders: none;

  /* Separadores verticais entre colunas no header */
  --ag-header-column-separator-display: block;
  --ag-header-column-separator-color: #cbd5e1;
  --ag-header-column-separator-height: 50%;

  /* Menus / popups de filtro */
  --ag-menu-background-color: #ffffff;
  --ag-menu-border-color: #e2e8f0;
  --ag-popup-shadow: 0 8px 24px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.06);
  --ag-border-radius: 10px;

  /* Inputs nos filtros */
  --ag-input-border-color: #cbd5e1;
  --ag-input-focus-border-color: #3b82f6;
  --ag-input-focus-box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  --ag-input-background-color: #ffffff;

  /* Checkbox */
  --ag-checkbox-checked-color: #3b82f6;
  --ag-checkbox-border-radius: 4px;
}

/* =========================================================
   CABEÇALHO — BORDAS ENTRE COLUNAS
   ========================================================= */

/* Borda inferior sólida do header */
:deep(.ag-header) {
  border-bottom: 2px solid #e2e8f0 !important;
}

/* Cada célula do header com borda direita visível */
:deep(.ag-header-cell) {
  border-right: 2px solid #e2e8f0 !important;
}

:deep(.ag-header-cell:last-child) {
  border-right: none !important;
}

/* Centraliza label */
:deep(.ag-center-header .ag-header-cell-label) {
  justify-content: center !important;
}

:deep(.ag-header-cell-text) {
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  color: #475569;
}

/* Ícone de sort/filtro no header */
:deep(.ag-header-icon) {
  color: #94a3b8;
}

:deep(.ag-header-cell-menu-button:hover .ag-icon) {
  color: #3b82f6;
}

/* Remove borda padrão do wrapper */
:deep(.ag-root-wrapper) {
  border: none !important;
  border-radius: 12px;
}

/* Linha divisória das células do body */
:deep(.ag-cell) {
  border-right: 1px solid #f1f5f9 !important;
}

:deep(.ag-cell:last-child) {
  border-right: none !important;
}

/* =========================================================
   MENU DE FILTRO — ESTILIZAÇÃO COMPLETA
   ========================================================= */

/* Container do popup */
:deep(.ag-filter) {
  border-radius: 10px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10) !important;
  overflow: hidden;
  min-width: 220px;
}

/* Tabs do filtro (Filtro / Colunas) */
:deep(.ag-tabs-header) {
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 0 8px !important;
}

:deep(.ag-tab) {
  padding: 10px 14px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #94a3b8 !important;
  border-bottom: 2px solid transparent !important;
  transition: all 0.15s;
}

:deep(.ag-tab-selected) {
  color: #3b82f6 !important;
  border-bottom-color: #3b82f6 !important;
}

/* Corpo do filtro */
:deep(.ag-filter-body-wrapper) {
  padding: 12px !important;
  background: white !important;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Wrapper do campo de texto — remove posicionamento relativo que causa sobreposição */
:deep(.ag-filter-body-wrapper .ag-text-field),
:deep(.ag-filter-body-wrapper .ag-number-field),
:deep(.ag-mini-filter .ag-text-field) {
  display: block !important;
  position: static !important;
  width: 100% !important;
}

/* Esconde o ícone de lupa e a label flutuante que ficam sobrepostos */
:deep(.ag-filter-body-wrapper .ag-text-field-input-wrapper::before),
:deep(.ag-filter-body-wrapper .ag-text-field-input-wrapper::after),
:deep(.ag-filter-body-wrapper .ag-input-field-label),
:deep(.ag-filter-body-wrapper .ag-label),
:deep(.ag-filter-body-wrapper .ag-text-field .ag-icon),
:deep(.ag-filter-body-wrapper .ag-number-field .ag-icon) {
  display: none !important;
}

/* Input em si — limpo e sem sobreposição */
:deep(.ag-filter-body-wrapper .ag-text-field-input),
:deep(.ag-filter-body-wrapper .ag-number-field-input) {
  width: 100% !important;
  padding: 8px 10px !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-family: 'Inter', sans-serif !important;
  color: #334155 !important;
  background: #f8fafc !important;
  outline: none !important;
  box-sizing: border-box !important;
  position: static !important;
  transition: border-color 0.15s, box-shadow 0.15s;
}

:deep(.ag-filter-body-wrapper .ag-text-field-input:focus),
:deep(.ag-filter-body-wrapper .ag-number-field-input:focus) {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
  background: #ffffff !important;
}

/* Dropdown de tipo de filtro (Contains, Equals...) */
:deep(.ag-filter-select .ag-select-list),
:deep(.ag-filter-select .ag-picker-field-wrapper) {
  border: 1px solid #cbd5e1 !important;
  border-radius: 6px !important;
  background: #f8fafc !important;
  font-size: 13px !important;
}

:deep(.ag-select-list) {
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  overflow: hidden !important;
}

:deep(.ag-select-list-item) {
  padding: 8px 12px !important;
  font-size: 13px !important;
  color: #334155 !important;
  transition: background 0.1s;
}

:deep(.ag-select-list-item:hover),
:deep(.ag-select-list-item.ag-active-item) {
  background: #eff6ff !important;
  color: #3b82f6 !important;
}

/* Botões Apply / Reset / Clear no filtro */
:deep(.ag-filter-apply-panel) {
  padding: 10px 12px !important;
  background: #f8fafc !important;
  border-top: 1px solid #e2e8f0 !important;
  display: flex !important;
  gap: 8px !important;
  justify-content: flex-end !important;
}

:deep(.ag-filter-apply-panel-button) {
  padding: 6px 14px !important;
  border-radius: 6px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  border: 1px solid #cbd5e1 !important;
  background: white !important;
  color: #475569 !important;
  transition: all 0.15s !important;
}

:deep(.ag-filter-apply-panel-button:last-child) {
  background: #3b82f6 !important;
  border-color: #3b82f6 !important;
  color: white !important;
}

:deep(.ag-filter-apply-panel-button:hover) {
  opacity: 0.85 !important;
}

/* Lista de valores no set filter */
:deep(.ag-set-filter-list) {
  padding: 4px !important;
}

:deep(.ag-virtual-list-item) {
  padding: 0 4px !important;
}

:deep(.ag-set-filter-item) {
  border-radius: 4px !important;
  padding: 4px 6px !important;
  font-size: 13px !important;
  color: #334155 !important;
}

:deep(.ag-set-filter-item:hover) {
  background: #f1f5f9 !important;
}

/* Mini-filter (busca dentro do set filter) */
:deep(.ag-mini-filter) {
  padding: 8px 8px 4px 8px !important;
  display: block !important;
}

:deep(.ag-mini-filter .ag-text-field) {
  display: block !important;
  position: static !important;
  width: 100% !important;
}

/* Esconde ícone/label sobrepostos no mini-filter */
:deep(.ag-mini-filter .ag-input-field-label),
:deep(.ag-mini-filter .ag-label),
:deep(.ag-mini-filter .ag-icon) {
  display: none !important;
}

:deep(.ag-mini-filter .ag-text-field-input) {
  width: 100% !important;
  padding: 7px 10px !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-family: 'Inter', sans-serif !important;
  background: #f8fafc !important;
  color: #334155 !important;
  outline: none !important;
  box-sizing: border-box !important;
  position: static !important;
}

:deep(.ag-mini-filter .ag-text-field-input:focus) {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
  background: white !important;
}

/* =========================================================
   PAGINAÇÃO
   ========================================================= */
:deep(.ag-paging-panel) {
  height: 45px !important;
  min-height: 45px !important;
  background: #f8fafc !important;
  border-top: 2px solid #e2e8f0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
}

:deep(.ag-paging-row-summary-panel) {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #475569 !important;
}

:deep(.ag-paging-row-summary-panel span) {
  color: #3b82f6;
}

:deep(.ag-paging-page-summary-panel) {
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
}

:deep(.ag-paging-button) {
  width: 34px !important;
  height: 34px !important;
  border-radius: 7px !important;
  border: 1px solid #e2e8f0 !important;
  background: white !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.15s !important;
}

:deep(.ag-paging-button:hover:not(.ag-disabled)) {
  background: #3b82f6 !important;
  border-color: #3b82f6 !important;
}

:deep(.ag-paging-button:hover:not(.ag-disabled) .ag-icon) {
  color: white !important;
}

:deep(.ag-paging-button.ag-disabled) {
  opacity: 0.35 !important;
  cursor: not-allowed !important;
}

:deep(.ag-paging-button .ag-icon) {
  font-size: 15px !important;
  color: #64748b;
}

:deep(.ag-paging-description) {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  padding: 0 8px !important;
}

/* =========================================================
   SCROLLBARS
   ========================================================= */

/* Horizontal */
:deep(.ag-body-horizontal-scroll) {
  background: #f1f5f9;
}


:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar) {
  height: 11px !important;
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track) {
  background: #f1f5f9;
  border-radius: 99px;
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb) {
  background: #94a3b8;
  border-radius: 99px;
  border: 2px solid #f1f5f9;
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb:hover) {
  background: #3b82f6;
}

:deep(.ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb:active) {
  background: #2563eb;
}

/* Vertical — mesmo estilo */
:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar) {
  width: 11px;
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar-track) {
  background: #f1f5f9;
  border-radius: 99px;
  margin: 8px 0;
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb) {
  background: #94a3b8;
  border-radius: 99px;
  border: 2px solid #f1f5f9;
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb:hover) {
  background: #3b82f6;
}

:deep(.ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb:active) {
  background: #2563eb;
}

/* =========================================================
   DESTAQUE DE CÉLULA SELECIONADA
   ========================================================= */

:deep(.ag-cell-focus) {

  border: 2px solid #3b82f6 !important;


  background-color: rgba(59, 130, 246, 0.1) !important;


  font-weight: 700 !important;
  color: #1e40af !important;


  transition: all 0.1s ease-in-out;


  z-index: 2;
}

:deep(.ag-cell-focus:focus-visible) {
  outline: none !important;
}

:deep(.ag-row-selected .ag-cell-focus) {
  background-color: rgba(59, 130, 246, 0.2) !important;
  border-color: #2563eb !important;
}

@media (max-width: 743px) {

  .kanban-container {
    padding: 16px 20px;
    gap: 16px;
  }


  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }


  .controls-area {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    border: none;
  }


  .search-group {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    border: none;
  }

  .search-wrapper,
  .global-search {
    width: 100% !important;
  }

  .btn {
    width: 30%;
    justify-content: center;
  }

  .status-indicator {
    justify-content: center;
    width: 50%;
  }

  .grid-wrapper {
    height: calc(100vh - 320px);
  }
}

/* =========================================================
   PAGINAÇÃO — SELETOR DE TAMANHO DE PÁGINA (PAGE SIZE)
   ========================================================= */

/* Esconde o texto padrão "Page Size" ou "Tamanho da Página" */
:deep(.ag-paging-page-size .ag-paging-label) {
  display: none !important;
}

/* Garante que o container do seletor tenha largura suficiente */
:deep(.ag-paging-page-size) {
  display: flex !important;
  align-items: center !important;
  margin-right: 20px !important;
}

/* Adiciona o seu texto personalizado "Linhas:" */
:deep(.ag-paging-page-size::before) {
  content: "Linhas:";
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-right: 8px;
}

/* Estiliza o campo de seleção (o "retângulo" do número) */
:deep(.ag-paging-page-size .ag-picker-field-wrapper) {
  min-width: 75px !important;
  /* Largura para não cortar o "100" */
  height: 32px !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 6px !important;
  background-color: white !important;
  cursor: pointer;
}

/* Estiliza o número dentro do seletor */
:deep(.ag-paging-page-size .ag-picker-field-display) {
  font-weight: 700 !important;
  color: #3b82f6 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Ajusta o ícone da setinha no seletor */
:deep(.ag-paging-page-size .ag-picker-field-icon) {
  color: #94a3b8 !important;
  display: flex !important;
  align-items: center !important;
}
</style>
